import React, { useState } from 'react';
import { Stack, Text } from '@chakra-ui/react';
import { ItemModel } from 'models';
import { Condition } from 'models/types';
import Wizard from '../Wizard';
import ItemAskCard from 'components/Item/ItemAskCard/ItemAskCard';
import StepWrapper from '../StepWrapper';
import {
  FaDollarSign,
  FaClipboardList,
  FaImage,
  FaStickyNote,
  FaCheckCircle,
  FaCheck,
} from 'react-icons/fa';
import { IoLocationOutline } from 'react-icons/io5';
import ProgressBar from '../../ProgressBar/ProgressBar';

interface ItemAskGuideWizardProps {
  item: ItemModel;
}

export default function ItemAskGuideWizard({ item }: ItemAskGuideWizardProps) {
  const [activeStep, setActiveStep] = useState(0);
  const amount =
    item.getEstimatedMarketPrice() ||
    item.getReleaseConvertedPrice() ||
    1000;

  const step1Content = (
    <Stack>
      <ItemAskCard
        item={item}
        askAmount={amount}
        askCreatedDays={0}
        userOwnsAsk
        shorten
      />
      <StepWrapper
        stepKey="amount"
        icon={FaDollarSign}
        title="Step 1: Ask Amount"
        description="Specify your asking price"
        minH="260px"
      >
        <Text fontWeight={300} fontSize="sm" textAlign="left">
          Set an asking price for your item. Our pricing engine can guide you on fair market value.
        </Text>
      </StepWrapper>
    </Stack>
  );

  const step2Content = (
    <Stack>
      <ItemAskCard
        item={item}
        askAmount={amount}
        condition={Condition.GOOD}
        askCreatedDays={0}
        userOwnsAsk
        shorten
      />
      <StepWrapper
        stepKey="condition"
        icon={FaClipboardList}
        title="Step 2: Condition"
        description="Specify item's condition"
        minH="260px"
      >
        <Text fontWeight={300} fontSize="sm" textAlign="left">
          Indicate the condition of your item to help set expectations with buyers.
        </Text>
      </StepWrapper>

    </Stack>
  );

  const step3Content = (
    <Stack>
      <ItemAskCard
        item={item}
        askAmount={amount}
        condition={Condition.GOOD}
        askCreatedDays={0}
        userOwnsAsk
        shorten
      />
      <StepWrapper
        stepKey="images"
        icon={FaImage}
        title="Step 3: Upload Images"
        description="Optional reference photos"
        minH="260px"
      >
        <Text fontWeight={300} fontSize="sm" textAlign="left">
          Add a few photos so buyers can better evaluate your item. Please highlight any condition issues.
        </Text>
      </StepWrapper>
    </Stack>
  );

  const step4Content = (
    <Stack>
      <ItemAskCard
        item={item}
        askAmount={amount}
        condition={Condition.GOOD}
        askCreatedDays={0}
        userOwnsAsk
        shorten
      />
      <StepWrapper
        stepKey="notes"
        icon={FaStickyNote}
        title="Step 4: Add Notes"
        description="Provide any extra details"
        minH="260px"
      >
        <Text fontWeight={300} fontSize="sm" textAlign="left">
          Share any additional information about your item that might help buyers.
        </Text>
      </StepWrapper>
    </Stack>
  );

  const step5Content = (
    <Stack>
      <ItemAskCard
        item={item}
        askAmount={amount}
        condition={Condition.GOOD}
        countryCode={'US'}
        askCreatedDays={0}
        userOwnsAsk
        shorten
      />
      <StepWrapper
        stepKey="address"
        icon={IoLocationOutline}
        title="Step 5: Pickup Address"
        description="Confirm where buyers can pick up"
        minH="260px"
      >
        <Text fontWeight={300} fontSize="sm" textAlign="left">
          Your primary address is selected by default but can be changed if needed.
        </Text>
      </StepWrapper>
    </Stack>
  );

  const step6Content = (
    <Stack>
      <ItemAskCard
        item={item}
        askAmount={amount}
        askCreatedDays={0}
        condition={Condition.GOOD}
        countryCode={'US'}
        userOwnsAsk
        shorten
      />
      <StepWrapper
        stepKey="confirm"
        icon={FaCheckCircle}
        title="Step 6: Review & Submit"
        description="Confirm your ask"
        minH="260px"
      >
        <Stack spacing={4}>
          <Text fontWeight={300} fontSize="sm" textAlign="left">
            Review everything one last time before submitting. You can edit your ask after placing it.
          </Text>
        </Stack>
      </StepWrapper>
    </Stack>
  );

  const wizardSteps = [
    { label: '', icon: FaDollarSign, content: step1Content },
    { label: '', icon: FaClipboardList, content: step2Content },
    { label: '', icon: FaImage, content: step3Content },
    { label: '', icon: FaStickyNote, content: step4Content },
    { label: '', icon: IoLocationOutline, content: step5Content },
    { label: '', icon: FaCheckCircle, content: step6Content, requiresValidation: true, isValidated: false },
  ];

  const totalSteps = wizardSteps.length;
  const progressPercentage = ((activeStep + 1) / totalSteps) * 100;
  const progressIcon = activeStep === totalSteps - 1 ? FaCheck : wizardSteps[activeStep].icon;

  return (
    <Stack width="100%">
      <ProgressBar progress={progressPercentage} icon={progressIcon} dynamicColor height={4} />
      <Wizard steps={wizardSteps} showStepper={false} onStepChange={setActiveStep} />
    </Stack>
  );
}
